import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardBody, Spinner, Button, Input } from '@material-tailwind/react';
import { FaArrowLeft, FaEnvelope, FaPrint, FaDownload, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { openWhatsapp } from '../../../utils/whatsapp';
import { DEFAULT_COMPANY_INFO, fetchCompanyInfo } from '../../../utils/companyInfo';
import OwnerTransactionPrintSheet from './OwnerTransactionPrintSheet';
import { formatMoney } from './formatMoney';
import { buildOwnerTransactionFilename, generateOwnerTransactionPdf } from '../../../utils/ownerTransactionPDFGenerator';

const TYPE_LABELS = {
  REM: 'Remittance', PAY: 'Payment', RIM: 'Reimbursement',
  CHG: 'Charge', ADJ: 'Adjustment', MOI: 'Monthly Invoice',
  JIN: 'Job Card Invoice', REV: 'Reversal', CRM: 'Cancel Remittance',
  RRM: 'Reverse Remittance', RPY: 'Reverse Payment', RRI: 'Reverse Reimbursement',
  RCG: 'Reverse Charge', RAD: 'Reverse Adjustment', RMI: 'Reverse Monthly Invoice',
};

const REVERSAL_TYPES = new Set(['REV', 'RRM', 'RPY', 'RRI', 'RCG', 'RAD', 'RJI', 'CRM', 'RMI']);

const getDocumentLabel = (t) => TYPE_LABELS[String(t?.trans_type || '').toUpperCase()] || t?.short_desc || t?.trans_type || 'Transaction';

const isReversed = (t) =>
  t?.reversed === true || t?.reversed === 1 || t?.reversed === '1' ||
  String(t?.reference_type || '').toUpperCase() === 'REVERSAL';

const getStatus = (t) => {
  if (isReversed(t)) return 'REVERSED';
  const raw = String(t?.invoice_status || '').trim().toUpperCase();
  if (['DRAFT', 'SENT', 'PAID'].includes(raw)) return raw;
  return String(t?.trans_type || '').toUpperCase() === 'MOI' ? 'DRAFT' : '';
};

const shortDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const colours = {
    SENT: 'bg-blue-100 text-blue-700',
    PAID: 'bg-green-100 text-green-700',
    REVERSED: 'bg-red-100 text-red-700',
    DRAFT: 'bg-amber-100 text-amber-700',
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide ${colours[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
    <div className="text-sm font-semibold text-gray-800">{children}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{children}</h2>
);

// ─── Car Breakdown Table ───────────────────────────────────────────────────────

const CarBreakdownTable = ({ lines }) => {
  if (!lines.length) return null;
  const totals = lines.reduce((acc, l) => ({
    gross: acc.gross + Number(l.gross_amount || 0),
    charges: acc.charges + Number(l.discount_amount || 0),
    payout: acc.payout + Number(l.prorated_amount || l.base_amount || 0),
  }), { gross: 0, charges: 0, payout: 0 });

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Plate</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase">Gross</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase">Charges</th>
            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase">Payout</th>
            <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase">Days</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 bg-white">
          {lines.map((l, i) => (
            <tr key={i} className="hover:bg-slate-50/60">
              <td className="px-4 py-2.5 font-mono font-bold text-[#2a2e30]">
                {l.plate_no || '-'}
                {l.invoice_period && <span className="ml-2 text-[10px] text-gray-400 font-normal">{l.invoice_period}</span>}
              </td>
              <td className="px-4 py-2.5 text-right text-gray-700">{formatMoney(l.gross_amount || 0)}</td>
              <td className="px-4 py-2.5 text-right text-red-600">{formatMoney(l.discount_amount || 0)}</td>
              <td className="px-4 py-2.5 text-right font-semibold text-green-700">{formatMoney(l.prorated_amount || l.base_amount || 0)}</td>
              <td className="px-4 py-2.5 text-center text-gray-600">{l.days_counted || '-'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 border-t-2 border-gray-200">
          <tr>
            <td className="px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Total ({lines.length} cars)</td>
            <td className="px-4 py-2.5 text-right font-bold text-gray-700">{formatMoney(totals.gross)}</td>
            <td className="px-4 py-2.5 text-right font-bold text-red-600">{formatMoney(totals.charges)}</td>
            <td className="px-4 py-2.5 text-right font-bold text-green-700">{formatMoney(totals.payout)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

// ─── Email Panel ───────────────────────────────────────────────────────────────

const EmailPanel = ({ transaction, user, authToken, filename, getPdf, onStatus }) => {
  const [open, setOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(transaction?.owner_email || '');
  const [emailMessage, setEmailMessage] = useState(
    transaction?.owner_name
      ? `Hello ${transaction.owner_name},\n\nPlease find attached the ${getDocumentLabel(transaction).toLowerCase()} document.\n\nRegards,\n${user?.username || 'Fleet Management'}`
      : `Please find attached the ${getDocumentLabel(transaction).toLowerCase()} document.`
  );
  const [busy, setBusy] = useState(false);

  const sendEmail = async () => {
    if (!recipientEmail) { onStatus({ type: 'error', text: 'Recipient email required.' }); return; }
    try {
      setBusy(true);
      const pdf = await getPdf();
      const blob = pdf.output('blob');
      const form = new FormData();
      form.append('recipient_email', recipientEmail);
      form.append('subject', `${transaction.owner_name || transaction.owner_code} - ${getDocumentLabel(transaction)}`);
      form.append('message', emailMessage);
      form.append('filename', filename);
      form.append('pdf_file', blob, filename);
      const res = await axios.post(
        `${rootUrl}/api/owners/${encodeURIComponent(transaction.owner_code)}/transactions/${encodeURIComponent(transaction.id)}/send-email`,
        form,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      onStatus(res.data?.success ? { type: 'success', text: 'Email sent successfully.' } : { type: 'error', text: res.data?.message || 'Send failed.' });
      if (res.data?.success) setOpen(false);
    } catch (e) {
      onStatus({ type: 'error', text: e.response?.data?.message || e.message || 'Send failed.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardBody className="p-0">
        <button
          onClick={() => setOpen(o => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition rounded-xl"
        >
          <span className="flex items-center gap-2"><FaEnvelope className="text-gray-400" /> Send by Email</span>
          {open ? <FaChevronUp className="text-gray-400 text-xs" /> : <FaChevronDown className="text-gray-400 text-xs" />}
        </button>
        {open && (
          <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
            <div className="pt-4">
              <Input
                type="email"
                label="Recipient email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
            </div>
            <textarea
              rows={5}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#FF5733] resize-none"
            />
            <Button
              onClick={sendEmail}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#FF5733] text-white shadow-none text-sm normal-case"
            >
              <FaEnvelope className="w-3.5 h-3.5" />
              {busy ? 'Sending…' : 'Send Email'}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const OwnerTransactionDetails = () => {
  const { ownerCode, transId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const authToken = token || localStorage.getItem('authToken');
  const [transaction, setTransaction] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [txRes, company] = await Promise.all([
          axios.get(`${rootUrl}/api/owners/${encodeURIComponent(ownerCode)}/transactions/${encodeURIComponent(transId)}`, { headers: { Authorization: `Bearer ${authToken}` } }),
          fetchCompanyInfo(authToken).catch(() => DEFAULT_COMPANY_INFO),
        ]);
        if (!active) return;
        if (txRes.data?.success) setTransaction(txRes.data.data);
        else setError(txRes.data?.message || 'Transaction not found');
        setCompanyInfo(company || DEFAULT_COMPANY_INFO);
      } catch (e) {
        if (!active) return;
        setError(e.response?.data?.message || 'Failed to load transaction');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (ownerCode && transId) load();
    else { setLoading(false); setError('Owner code and transaction ID are required'); }
    return () => { active = false; };
  }, [authToken, ownerCode, transId]);

  const filename = useMemo(() => transaction ? buildOwnerTransactionFilename(transaction) : '', [transaction]);
  const getPdf = () => generateOwnerTransactionPdf();

  const downloadPdf = async () => {
    try {
      setActionBusy('download');
      const pdf = await getPdf();
      pdf.save(filename);
      setStatusMsg({ type: 'success', text: 'PDF downloaded.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message || 'Download failed.' });
    } finally { setActionBusy(''); }
  };

  const printPdf = async () => {
    let frame = null;
    try {
      setActionBusy('print');
      const pdf = await getPdf();
      const url = pdf.output('bloburl');
      frame = document.createElement('iframe');
      Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0', visibility: 'hidden' });
      frame.src = url;
      document.body.appendChild(frame);
      frame.onload = () => {
        try { frame.contentWindow.focus(); frame.contentWindow.print(); }
        catch { window.open(url, '_blank', 'noopener,noreferrer'); }
      };
      setTimeout(() => frame?.parentNode?.removeChild(frame), 30000);
    } catch (e) {
      frame?.parentNode?.removeChild(frame);
      setStatusMsg({ type: 'error', text: e.message || 'Print failed.' });
    } finally { setActionBusy(''); }
  };

  const shareWhatsApp = async () => {
    try {
      setActionBusy('whatsapp');
      const res = await axios.post(
        `${rootUrl}/api/owners/${encodeURIComponent(transaction.owner_code)}/transactions/${encodeURIComponent(transaction.id)}/share-link`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const token = res.data?.data?.share_token;
      if (!token) throw new Error('No share link returned');
      const url = `${rootUrl}/share/owner-document/${encodeURIComponent(token)}?download=1`;
      const text = [`${getDocumentLabel(transaction)} document`, transaction.owner_name ? `Owner: ${transaction.owner_name}` : '', `Ref: ${transaction.id}`, `Download: ${url}`].filter(Boolean).join('\n');
      openWhatsapp(transaction?.owner_phone, text);
      setStatusMsg({ type: 'success', text: 'WhatsApp opened.' });
    } catch (e) {
      setStatusMsg({ type: 'error', text: e.message || 'Share failed.' });
    } finally { setActionBusy(''); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] flex items-center justify-center">
      <Spinner className="h-12 w-12" color="orange" />
    </div>
  );

  if (error || !transaction) return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
      <Card className="max-w-xl mx-auto border-0 shadow-xl">
        <CardBody className="p-6">
          <p className="text-sm text-red-700 bg-red-50 rounded-lg px-4 py-3 mb-4">{error || 'Transaction not found'}</p>
          <Button onClick={() => navigate('/owners/transactions')} className="bg-[#FF5733] shadow-none">Back to Transactions</Button>
        </CardBody>
      </Card>
    </div>
  );

  const type = String(transaction.trans_type || '').toUpperCase();
  const docNo = String(transaction.id || transId || '').split(':').pop() || '-';
  const detailLines = Array.isArray(transaction.detail_lines) ? transaction.detail_lines : [];
  const reversedDoc = isReversed(transaction);
  const status = getStatus(transaction);
  const isEditable = status === 'DRAFT' && !reversedDoc;
  const grossAmount = Number(transaction.gross_amount || detailLines.reduce((s, l) => s + Number(l.gross_amount || 0), 0) || transaction.amount || 0);
  const discountAmount = Number(transaction.discount_amount || detailLines.reduce((s, l) => s + Number(l.discount_amount || 0), 0) || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">

      {/* Hidden print sheet — kept in DOM for PDF generation */}
      <div className="sr-only" aria-hidden="true">
        <OwnerTransactionPrintSheet transaction={transaction} companyInfo={companyInfo} />
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6 pb-10">

        {/* ── Header bar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition"
            >
              <FaArrowLeft className="w-3 h-3" /> Back
            </button>
            <div className="h-4 w-px bg-gray-300" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-[#2a2e30]">
                  {getDocumentLabel(transaction)} <span className="font-mono text-[#FF5733]">#{docNo}</span>
                </h1>
                {status && <StatusBadge status={status} />}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                <Link to={`/owners/statements/${transaction.owner_code}`} className="hover:underline text-[#FF5733] font-medium">
                  {transaction.owner_name || transaction.owner_code}
                </Link>
                {' · '}{shortDate(transaction.date)}
                {transaction.week_key && <>{' · '}Week {transaction.week_key}</>}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {type === 'MOI' && isEditable && (
              <Button
                onClick={() => navigate(`/owners/invoices/monthly/edit/${encodeURIComponent(transaction.owner_code)}/${encodeURIComponent(transaction.id)}`)}
                className="bg-amber-500 shadow-none text-sm py-2 normal-case"
              >
                Edit Invoice
              </Button>
            )}
            <button
              onClick={printPdf}
              disabled={!!actionBusy}
              title="Print"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:border-[#FF5733] hover:text-[#FF5733] transition disabled:opacity-50"
            >
              <FaPrint className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{actionBusy === 'print' ? 'Printing…' : 'Print'}</span>
            </button>
            <button
              onClick={downloadPdf}
              disabled={!!actionBusy}
              title="Download PDF"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:border-teal-600 hover:text-teal-600 transition disabled:opacity-50"
            >
              <FaDownload className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{actionBusy === 'download' ? 'Preparing…' : 'Download'}</span>
            </button>
            <button
              onClick={shareWhatsApp}
              disabled={!!actionBusy}
              title="Share on WhatsApp"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-200 bg-white text-sm text-green-600 hover:bg-green-50 transition disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faWhatsapp} />
              <span className="hidden sm:inline">{actionBusy === 'whatsapp' ? 'Opening…' : 'WhatsApp'}</span>
            </button>
          </div>
        </div>

        {/* Reversed warning */}
        {reversedDoc && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-100 border border-red-200 px-4 py-3 text-sm font-semibold text-red-700">
            This transaction has been reversed and is no longer active.
          </div>
        )}

        {/* Status message */}
        {statusMsg.text && (
          <div className={`mb-4 rounded-xl px-4 py-3 text-sm font-semibold border ${statusMsg.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            {statusMsg.text}
          </div>
        )}

        {/* ── Key details card ── */}
        <Card className="border-0 shadow-sm mb-4">
          <CardBody className="p-5">
            {/* Amount highlight */}
            <div className="flex items-end justify-between mb-5 pb-5 border-b border-gray-100">
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Net Amount</p>
                <p className="text-3xl font-bold text-[#2a2e30]">{formatMoney(transaction.amount || 0)}</p>
                {grossAmount > 0 && grossAmount !== Number(transaction.amount || 0) && (
                  <p className="text-xs text-gray-400 mt-1">
                    Gross {formatMoney(grossAmount)}
                    {discountAmount > 0 && <> · Charges <span className="text-red-500">{formatMoney(discountAmount)}</span></>}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">GL Posted</p>
                <span className={`text-sm font-bold ${transaction.gl_posted ? 'text-green-600' : 'text-amber-500'}`}>
                  {transaction.gl_posted ? 'Posted' : 'Pending'}
                </span>
              </div>
            </div>

            {/* Field grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <Field label="Owner">
                <Link to={`/owners/statements/${transaction.owner_code}`} className="text-[#FF5733] hover:underline">
                  {transaction.owner_name || transaction.owner_code}
                </Link>
              </Field>
              <Field label="Type">
                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-bold">{type}</span>
              </Field>
              <Field label="Date">{shortDate(transaction.date)}</Field>
              {transaction.week_key && <Field label="Week">{transaction.week_key}</Field>}
              {transaction.plate_no && (
                <Field label="Car">
                  <Link to={`/cars/${transaction.plate_no}`} className="text-[#FF5733] hover:underline font-mono">{transaction.plate_no}</Link>
                </Field>
              )}
              <Field label="Document No"><span className="font-mono">{docNo}</span></Field>
              {transaction.input_by && <Field label="Posted by">{transaction.input_by}</Field>}
              {transaction.reference_type && <Field label="Ref Type">{transaction.reference_type}</Field>}
            </div>
          </CardBody>
        </Card>

        {/* ── Car breakdown ── */}
        {(type === 'REM' || type === 'MOI') && detailLines.length > 0 && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody className="p-5">
              <SectionTitle>Car Breakdown</SectionTitle>
              <CarBreakdownTable lines={detailLines} />
            </CardBody>
          </Card>
        )}

        {/* ── Payment details ── */}
        {(type === 'PAY' || type === 'RIM') && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody className="p-5">
              <SectionTitle>Payment Details</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
                {transaction.cashbook_entry_id && <Field label="Cashbook Entry">{transaction.cashbook_entry_id}</Field>}
                {transaction.payment_method && <Field label="Method">{transaction.payment_method}</Field>}
                {transaction.payment_reference && <Field label="Reference">{transaction.payment_reference}</Field>}
                {!transaction.cashbook_entry_id && !transaction.payment_method && (
                  <p className="col-span-3 text-sm text-gray-400">No additional payment details recorded.</p>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* ── MOI invoice status ── */}
        {type === 'MOI' && (
          <Card className={`border-0 shadow-sm mb-4 ${status === 'PAID' ? 'bg-green-50' : status === 'SENT' ? 'bg-blue-50' : ''}`}>
            <CardBody className="p-5">
              <SectionTitle>Invoice Status</SectionTitle>
              <div className="flex items-center gap-3">
                <StatusBadge status={status} />
                {transaction.invoice_period && <span className="text-sm text-gray-500">Period: <strong>{transaction.invoice_period}</strong></span>}
                {status !== 'DRAFT' && <span className="text-xs text-gray-400">Cannot be edited without admin unlock</span>}
              </div>
            </CardBody>
          </Card>
        )}

        {/* ── Job card link ── */}
        {type === 'JIN' && transaction.reference_id && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody className="p-5">
              <SectionTitle>Linked Job Card</SectionTitle>
              <div className="flex items-center gap-3">
                <Link to={`/job-cards/${encodeURIComponent(transaction.reference_id)}`} className="text-[#FF5733] hover:underline font-mono font-bold">
                  {transaction.reference_id}
                </Link>
                {transaction.reference_type && <span className="text-sm text-gray-400">{transaction.reference_type}</span>}
              </div>
            </CardBody>
          </Card>
        )}

        {/* ── Reversal info ── */}
        {(reversedDoc || REVERSAL_TYPES.has(type)) && (
          <Card className="border-0 shadow-sm mb-4 bg-red-50">
            <CardBody className="p-5">
              <SectionTitle>Reversal Information</SectionTitle>
              <div className="flex flex-wrap gap-6">
                {transaction.reference_id && (
                  <Field label="Reverses Transaction">
                    <Link to={`/owners/transactions/${encodeURIComponent(transaction.owner_code)}/${encodeURIComponent(transaction.reference_id)}`} className="text-red-600 hover:underline font-mono">
                      {transaction.reference_id}
                    </Link>
                  </Field>
                )}
                {transaction.reversed_ref && (
                  <Field label="Reversal Document">
                    <Link to={`/owners/transactions/${encodeURIComponent(transaction.owner_code)}/${encodeURIComponent(transaction.reversed_ref)}`} className="text-red-600 hover:underline font-mono">
                      {transaction.reversed_ref}
                    </Link>
                  </Field>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* ── Email panel ── */}
        <EmailPanel
          transaction={transaction}
          user={user}
          authToken={authToken}
          filename={filename}
          getPdf={getPdf}
          onStatus={setStatusMsg}
        />

      </div>
    </div>
  );
};

export default OwnerTransactionDetails;
